import React from 'react';
import {
  createCommand,
  DecoratorNode,
  DOMConversionMap,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import { Icon, Label } from 'semantic-ui-react';
import { getFileTypeInfo } from 'src/utils/helper';

export type AttachedNodePayload = {
  url: string;
  fileName: string;
  fileType: string;
};

export type SerializedAttachedNode = Spread<
  AttachedNodePayload & { type: 'attached_file' },
  SerializedLexicalNode
>;

export class AttachedNode extends DecoratorNode<React.ReactElement> {
  __url: string;
  __fileName: string;
  __fileType: string;

  constructor(url: string, fileName: string, fileType: string, key?: NodeKey) {
    super(key);
    this.__url = url;
    this.__fileName = fileName;
    this.__fileType = fileType;
  }

  getUrl(): string {
    return this.__url;
  }

  setUrl(url: string): void {
    this.getWritable().__url = url;
  }

  getFileType(): string {
    return this.__fileType;
  }

  setFileType(fileType: string): void {
    this.getWritable().__fileType = fileType;
  }

  getFileName(): string {
    return this.__fileName;
  }

  getInfo() {
    return getFileTypeInfo({ type: this.__fileType });
  }

  getClassName() {
    return 'd-flex p-12 border-gray hoverable border-radius-4 mb-8';
  }

  setFileName(fileName: string): void {
    this.getWritable().__fileName = fileName;
  }

  static getType(): string {
    return 'attached_file';
  }

  static clone(node: AttachedNode): AttachedNode {
    return new AttachedNode(
      node.__url,
      node.__fileName,
      node.__fileType,
      node.__key,
    );
  }

  static importJSON(serializedNode: SerializedAttachedNode): AttachedNode {
    return $createAttachedNode({
      url: serializedNode.url,
      fileType: serializedNode.fileType,
      fileName: serializedNode.fileName,
    });
  }

  exportJSON(): SerializedAttachedNode {
    return {
      version: 1,
      type: 'attached_file',
      url: this.getUrl(),
      fileName: this.getFileName(),
      fileType: this.getFileType(),
    };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      a: (node) => {
        if (!node.getAttribute('data-file-name')) {
          return null;
        }
        return {
          conversion: (element, parent) => {
            if (element instanceof HTMLAnchorElement) {
              const { href } = element;
              const fileName = element.getAttribute('data-file-name')!;
              const fileType = element.getAttribute('data-file-type')!;

              return {
                node: $createAttachedNode({ url: href, fileType, fileName }),
              };
            }

            return null;
          },
          priority: 2,
        };
      },
    };
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const element = document.createElement('a');
    element.setAttribute('class', 'ui label');
    element.setAttribute('href', this.getUrl());
    element.setAttribute('data-file-type', this.getFileType());
    element.setAttribute('data-file-name', this.getFileName());
    element.setAttribute('target', '_blank');

    const icon = document.createElement('i');
    icon.setAttribute('class', `icon ${this.getInfo().icon}`);
    icon.setAttribute('aria-hidden', 'true');

    const span = document.createElement('span');
    span.innerHTML = this.getFileName();

    element.append(icon, span);

    return { element };
  }

  createDOM(_config: EditorConfig, _editor: LexicalEditor): HTMLElement {
    const span = document.createElement('span');
    const theme = _config.theme;
    const className = theme.attached;
    if (className !== undefined) {
      span.className = className;
    }
    return span;
  }

  updateDOM(
    _prevNode: unknown,
    _dom: HTMLElement,
    _config: EditorConfig,
  ): boolean {
    return false;
  }

  decorate(editor: LexicalEditor, config: EditorConfig): React.ReactElement {
    return (
      <Label
        as="a"
        size="large"
        href={this.getUrl() || undefined}
        target="_blank"
        onRemove={() => console.log(this.getUrl())}
      >
        <Icon name={this.getInfo().icon} loading={this.getInfo().loading} />
        <span>{this.getFileName()}</span>
      </Label>
    );
  }
}

export const $createAttachedNode = ({
  url,
  fileType,
  fileName,
}: Partial<AttachedNodePayload>) => {
  const payload = {
    fileName: '...',
    fileType: 'loading',
    url: '',
  };

  return new AttachedNode(
    url || payload.url,
    fileName || payload.fileName,
    fileType || payload.fileType,
  );
};

export const $isAttachedNode = (node: any): node is AttachedNode => {
  return node instanceof AttachedNode;
};

export const INSERT_ATTACHED_NODE_COMMAND =
  createCommand<AttachedNodePayload>();

export const INSERT_LOADING_ATTACHED_NODE_COMMAND =
  createCommand<Partial<AttachedNodePayload>>();
